// API client for Assessment tests (/api/assessment/admin/tests, .../sections,
// /api/assessment/admin/test-types).
//
// Envelope: { data, isError, statusCode, errorMessage }. The tests list is a
// page { content, page, size, totalElements, totalPages, hasNext } whose rows
// are { test, testTypeName } wrapping the raw test entity. GET /tests/{id}
// returns { test, sections, resolvedQuestions, testTypeName, distribution }.
//
// Lifecycle: a test is created as DRAFT; only DRAFT tests can have their
// distribution changed and be published. Publishing resolves the distribution
// into a fixed question snapshot (fails if there are too few matching
// questions). DELETE hard-deletes a DRAFT test and archives (soft) any other.
import api from "../axiosInstance";

const BASE = "/api/assessment/admin";

export { apiErrorMessage } from "./assessmentHierarchy";

const unwrap = (res) => res.data?.data;

export const TEST_STATUSES = ["DRAFT", "PUBLISHED", "ARCHIVED"];

export const TEST_DIFFICULTIES = [
  { value: "EASY", label: "Easy" },
  { value: "MODERATE", label: "Moderate" },
  { value: "DIFFICULT", label: "Difficult" },
  { value: "VERY_DIFFICULT", label: "Very Difficult" },
];

// One colour per difficulty so every difficulty visual reads the same way.
export const DIFFICULTY_COLORS = {
  EASY: "#2e7d32",
  MODERATE: "#b26a00",
  DIFFICULT: "#c62828",
  VERY_DIFFICULT: "#6a1b9a",
};

export const TEST_QUESTION_PURPOSES = [
  { value: "PRACTICE_EXAMPLE", label: "Practice Example" },
  { value: "EXERCISE", label: "Exercise" },
  { value: "ASSESSMENT", label: "Assessment" },
];

export const DISTRIBUTION_LEVELS = [
  { value: "SUBJECT", label: "Subject (legacy)" },
  { value: "CHAPTER", label: "Chapters under subjects (Mock / PYQ)" },
  { value: "TOPIC", label: "Topics of one chapter (Chapter-wise)" },
  { value: "TOPIC_SUBTOPIC", label: "Topics / Sub-Topics" },
];

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );

// Flatten a list row { test, testTypeName } into one object.
const normalizeListRow = (row) => ({ ...row.test, testTypeName: row.testTypeName });

// filters: courseId, status, testTypeId. Returns the page envelope with flattened rows.
export const listTests = async ({ page = 0, size = 20, ...filters } = {}) => {
  const res = await api.get(`${BASE}/tests`, { params: cleanParams({ ...filters, page, size }) });
  const data = unwrap(res) || {};
  return { ...data, content: (data.content || []).map(normalizeListRow) };
};

// -> { test, sections, resolvedQuestions, testTypeName, distribution }
export const getTestDetail = (id) => api.get(`${BASE}/tests/${id}`).then(unwrap);

// Payload: courseId (create only; immutable afterwards), testTypeId, name,
// description, durationMinutes, instructionsText, requiresPremiumPlan, rotateQuestions (Practice tests only),
// displayOrder, totalQuestions, marksPerQuestion, negativeMarksPerQuestion,
// distribution: { items:[{subjectId,chapterId,topicId,subTopicId,questionCount}], difficultyCounts:{EASY,MODERATE,DIFFICULT,VERY_DIFFICULT} }.
export const createTest = (payload) => api.post(`${BASE}/tests`, payload).then(unwrap);
export const updateTest = (id, payload) => api.put(`${BASE}/tests/${id}`, payload).then(unwrap);
export const deleteTest = (id) => api.delete(`${BASE}/tests/${id}`).then(unwrap);
export const publishTest = (id) => api.post(`${BASE}/tests/${id}/publish`).then(unwrap);
export const duplicateTest = (id) => api.post(`${BASE}/tests/${id}/duplicate`).then(unwrap);
// -> { totalQuestions, durationMinutes, totalMarks, negativeMarksPerQuestion,
//      distributionLevel, difficultyDistribution[{difficulty,questionCount,percentage}],
//      hierarchyDistribution[{name,questionCount,percentage}] }
export const previewTest = (id) => api.get(`${BASE}/tests/${id}/preview`).then(unwrap);

// --- Weightage (DRAFT tests only) --------------------------------------------
// A weightage file (.xlsx / .csv) with Chapter, Topic, Mock Qs, Qs from PYQ and Qs from
// New-Assessment builds the test from exactly those counts instead of the Test Builder distribution.
// get -> { rows:[{sectionId,subjectName,chapterName,topicName,questionCount,pyqCount,newCount}],
//          totalQuestions, pyqQuestions, newQuestions } (rows empty when the test has none).
export const getTestWeightage = (id) => api.get(`${BASE}/tests/${id}/weightage`).then(unwrap);
export const uploadTestWeightage = (id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`${BASE}/tests/${id}/weightage`, formData).then(unwrap);
};
export const deleteTestWeightage = (id) => api.delete(`${BASE}/tests/${id}/weightage`).then(unwrap);

// Test Builder choices for a course + test type. Always returns subjects (with chapters); once subjectId and
// chapterId are passed (Chapter-wise / Practice types) it also returns topics (with sub-topics for Practice).
// -> { level, subjects:[{subjectId,name,chapters:[{chapterId,name,available}]}],
//      topics:[{topicId,name,available,subTopics:[{subTopicId,name,available}]}] }
// available = { total, byDifficulty: { EASY: n, MODERATE: n, ... } }
// subjectId / chapterId may each hold several ids (comma separated, e.g. subjectId: "2,3", chapterId: "7,9");
//   multi-chapter types (Mock / PYQ) then get chapterTopics:[{subjectId,chapterId,topics:[...same shape as
//   topics...]}] for every linked subject + chapter combination in one call.
export const getTestBuilderOptions = (params) =>
  api.get(`${BASE}/test-builder/options`, { params: cleanParams(params) }).then(unwrap);

// --- Sections (DRAFT tests only) -------------------------------------------
// Payload: name, displayOrder, subjectId, chapterId, topicId, subTopicId,
// difficulty, category, questionPurpose, questionCount, marksPerQuestion,
// negativeMarksPerQuestion.
export const listTestSections = (testId) =>
  api.get(`${BASE}/tests/${testId}/sections`).then(unwrap);
export const createTestSection = (testId, payload) =>
  api.post(`${BASE}/tests/${testId}/sections`, payload).then(unwrap);
export const updateTestSection = (testId, id, payload) =>
  api.put(`${BASE}/tests/${testId}/sections/${id}`, payload).then(unwrap);
export const deleteTestSection = (testId, id) =>
  api.delete(`${BASE}/tests/${testId}/sections/${id}`).then(unwrap);
export const reorderTestSections = (testId, sectionIds) =>
  api.put(`${BASE}/tests/${testId}/sections/reorder`, { sectionIds }).then(unwrap);

// --- Test types ------------------------------------------------------------
// Payload: name, active, distributionLevel (SUBJECT|CHAPTER|TOPIC|TOPIC_SUBTOPIC),
// questionUsage, questionPurposes.
// DELETE is soft (deactivates).
export const listTestTypes = ({ isActive } = {}) =>
  api.get(`${BASE}/test-types`, { params: cleanParams({ isActive }) }).then(unwrap);
// One page of test types: { content, page, size, totalElements, totalPages, hasNext }.
export const listTestTypesPage = ({ isActive, page = 0, size = 25 } = {}) =>
  api
    .get(`${BASE}/test-types`, { params: cleanParams({ isActive, page, size }) })
    .then((res) => unwrap(res) || { content: [], totalElements: 0, totalPages: 0 });
export const createTestType = (payload) => api.post(`${BASE}/test-types`, payload).then(unwrap);
export const updateTestType = (id, payload) => api.put(`${BASE}/test-types/${id}`, payload).then(unwrap);
export const deleteTestType = (id) => api.delete(`${BASE}/test-types/${id}`).then(unwrap);
